import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { csrfHeaders, toApiError } from '@/lib/http';

/**
 * Reader reports, in the review queue.
 *
 * A second kind of item, and **never buried among sign-offs**: its own tab,
 * its own count, and the reader's own words quoted in the row. A report is
 * rarer and more urgent than a routine approval — it names a passage and
 * somebody took the trouble to write about it.
 *
 * Every action states **what the reader sees afterwards**, because the
 * reporter is waiting on exactly that and cannot be emailed.
 */

interface ReportItem {
  readonly id: string;
  readonly khutbahId: string;
  readonly titleAr: string | null;
  readonly passageOrdinal: number;
  readonly language: string;
  readonly kind: string;
  readonly status: string;
  readonly notes: readonly string[];
  readonly reportCount: number;
}

const KIND_LABEL: Record<string, string> = {
  translation: 'Meaning wrong or missing',
  learning: 'Word meaning or grammar',
  citation: 'Qur’an or hadith reference',
  other: 'Something else',
};

/** The sentence each action sends back to the reader. */
const READER_SEES: Record<string, string> = {
  accepted: 'Being checked.',
  rejected: 'Looked at — the translation stands.',
  resolved: 'Fixed after your report.',
};

export function ReportsTab() {
  const queryClient = useQueryClient();

  const reports = useQuery({
    queryKey: ['reports', 'open'],
    queryFn: async ({ signal }): Promise<ReportItem[]> => {
      const response = await fetch('/api/editor/reports?status=open', {
        signal,
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw await toApiError(response);
      const body: unknown = await response.json();
      if (!Array.isArray(body)) return [];
      return body.flatMap((entry): ReportItem[] => {
        const row = entry as Record<string, unknown>;
        if (typeof row.id !== 'string') return [];
        return [
          {
            id: row.id,
            khutbahId: typeof row.khutbahId === 'string' ? row.khutbahId : '',
            titleAr: typeof row.titleAr === 'string' ? row.titleAr : null,
            passageOrdinal:
              typeof row.passageOrdinal === 'number' ? row.passageOrdinal : 0,
            language: typeof row.language === 'string' ? row.language : '',
            kind: typeof row.kind === 'string' ? row.kind : 'other',
            status: typeof row.status === 'string' ? row.status : 'open',
            notes: Array.isArray(row.notes)
              ? row.notes.filter((n): n is string => typeof n === 'string')
              : [],
            reportCount:
              typeof row.reportCount === 'number' ? row.reportCount : 1,
          },
        ];
      });
    },
  });

  const resolve = useMutation({
    retry: false,
    mutationFn: async (input: { item: ReportItem; status: string }) => {
      const response = await fetch('/api/editor/reports/resolve', {
        method: 'POST',
        headers: {
          ...(await csrfHeaders(new AbortController().signal)),
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          khutbahId: input.item.khutbahId,
          passage: input.item.passageOrdinal,
          language: input.item.language,
          kind: input.item.kind,
          status: input.status,
        }),
      });
      if (!response.ok) throw await toApiError(response);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['reports'] });
    },
  });

  if (reports.isPending) return <Skeleton className="h-40" />;
  if (reports.error) {
    return (
      <p role="alert" className="m-4 rounded-md border border-destructive p-3 text-sm">
        Could not load the reports.
      </p>
    );
  }

  if (reports.data.length === 0) {
    return (
      <div className="m-4 rounded-lg border border-dashed border-border p-8 text-center">
        <p className="font-semibold">No reports waiting</p>
        <p className="mx-auto mt-1 max-w-md text-sm text-pretty text-muted-foreground">
          Readers can flag a passage from the reader. Their reports arrive
          here, grouped by the passage and problem they describe.
        </p>
      </div>
    );
  }

  return (
    <ul className="flex flex-col">
      {reports.data.map((item) => (
        <li key={item.id} className="border-b border-border p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="flex flex-wrap items-center gap-2 text-sm font-semibold">
                {/* The count is the signal: three readers on one passage is
                    much stronger than one, and it leads the row. */}
                {item.reportCount > 1 ? (
                  <span className="rounded-sm bg-warning-bg px-1.5 py-0.5 text-warning">
                    {item.reportCount} readers
                  </span>
                ) : null}
                Passage {item.passageOrdinal + 1} · {item.language}
                <span className="font-normal text-muted-foreground">
                  {KIND_LABEL[item.kind] ?? item.kind}
                </span>
              </p>
              {item.titleAr ? (
                <p lang="ar" dir="rtl" className="script-ar mt-0.5 text-sm">
                  {item.titleAr}
                </p>
              ) : null}
            </div>

            <Button type="button" variant="outline" size="sm" asChild>
              <a href={`?khutbah=${encodeURIComponent(item.khutbahId)}`}>Open</a>
            </Button>
          </div>

          {/* The reader's own words, quoted. Paraphrasing them into a
              category loses the thing that makes a report useful. */}
          <ul className="mt-2 flex flex-col gap-1.5">
            {item.notes.map((note, index) => (
              <li
                key={index}
                className="rounded-md bg-muted p-2.5 text-[13px] text-pretty"
              >
                “{note}”
              </li>
            ))}
          </ul>

          <div className="mt-3 flex flex-wrap gap-2">
            {(['accepted', 'resolved', 'rejected'] as const).map((status) => (
              <Button
                key={status}
                type="button"
                variant={status === 'rejected' ? 'ghost' : 'outline'}
                size="sm"
                disabled={resolve.isPending}
                // Every action says what the READER will see, because that
                // is the consequence and the reporter cannot be emailed.
                title={`The reader sees: “${READER_SEES[status]}”`}
                onClick={() => resolve.mutate({ item, status })}
              >
                {status === 'accepted'
                  ? 'Being checked'
                  : status === 'resolved'
                    ? 'Fixed'
                    : 'Translation stands'}
              </Button>
            ))}
            <span className="self-center text-xs text-muted-foreground">
              {item.reportCount === 1
                ? 'The reader sees your answer on the passage.'
                : `All ${item.reportCount} readers see the same answer.`}
            </span>
          </div>
        </li>
      ))}
    </ul>
  );
}
